/**
 * ASO-ATTR-001 — which environment a SHIPPED CLIENT BUILD is, as the device can actually tell.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ `process.env.MY_ENVIRONMENT` DOES NOT SURVIVE BUNDLING. THIS IS MEASURED, NOT REASONED.
 *
 * The previous version of this function was `process.env.MY_ENVIRONMENT || 'production'`, with a
 * docblock explaining that every build path sets that variable — `package.json`'s scripts,
 * `scripts/publish-ota.js`, and `scripts/staging/deploy.sh`. All of that is true, and all of it is
 * irrelevant, because those set the variable for the NODE process that runs the build. Only
 * `EXPO_PUBLIC_*` variables are inlined into the client bundle; everything else stays a literal
 * `process.env.X` lookup against a runtime shim that holds essentially just `NODE_ENV`.
 *
 * Verified against the artefact rather than the toolchain docs: the staging web bundle, built by
 * `deploy.sh` with `MY_ENVIRONMENT=staging` exported, ships
 *
 *     function E(){return process.env.MY_ENVIRONMENT||'production'}
 *
 * — the expression un-substituted, so it evaluates to `'production'` on every device, in every
 * environment. Meanwhile every `EXPO_PUBLIC_*` read in this app is gone from the bundle, replaced
 * by its value. That is the whole difference.
 *
 * The consequence was the precise conflation the property exists to prevent: `Routes.js` hard-codes
 * ONE PostHog API key, so staging and production emit into the same project, and `atlas_env` was
 * the only thing separating them — while reporting `production` for both. Every staging QA launch
 * would have landed in the production first-open funnel, correctly labelled as real.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE FIREBASE PROJECT ID, AND NOT A NEW `EXPO_PUBLIC_ENVIRONMENT`
 *
 * Adding another variable would mean adding it to `eas.json`, the `package.json` scripts,
 * `publish-ota.js`, `deploy.sh` and the repo's GitHub secrets — five places that must agree, any
 * one of which can be missed, producing exactly this failure again and just as silently.
 *
 * `EXPO_PUBLIC_FIREBASE_PROJECT_ID` is already inlined, already distinct per environment, and —
 * the part that matters — already LOAD-BEARING. It is how the app finds its Firebase project, so a
 * build carrying the wrong value cannot log anyone in. An environment marker that is wrong is
 * invisible; a Firebase project that is wrong is discovered immediately. Deriving the marker from
 * the value that cannot silently be wrong is what stops this from rotting again.
 *
 * The mapping is explicit and the fallback NEVER guesses `production` — same rule the backend's
 * `growth-environment.ts` follows, and now for the same reason rather than a different one: an
 * unrecognised project is reported as itself so it is greppable, and the documented queries filter
 * for `production` explicitly, so anything unlabelled drops OUT of the numbers instead of inflating
 * them. The old `'production'` default was defensible only while the marker was believed to be set
 * on every path; it is the wrong default for a value that can genuinely be absent.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ THE MAP BELOW NAMES THE PRODUCTION PROJECT, AND THAT TRIPPED THE STAGING DEPLOY GUARD.
 *
 * `scripts/staging/deploy.sh` refuses to upload a web bundle that carries a production identifier.
 * Its first version grepped the whole bundle for the bare string, so `PROJECT_ENVIRONMENTS` — which
 * has to name both environments in order to translate between them — made this file unshippable to
 * staging the moment it was added. On 2026-08-31 that guard fired for the first time ever, on this
 * file, and the bundle it refused was thoroughly staging: `projectId:"qarasay-staging"`, six
 * occurrences of the staging id against one of the production one, and that one was the key below.
 *
 * The guard now matches a production id in a CONFIG POSITION rather than anywhere in 11MB of
 * JavaScript, and carries a positive control so it cannot silently degrade into one that passes
 * everything. Do not "solve" a future recurrence here by renaming, splitting or obfuscating these
 * keys to dodge a grep — the map is correct and readable, and a lookup table that cannot be grepped
 * for the thing it maps is worse than the problem.
 *
 * ⚠️ AND DO NOT WIDEN THAT GUARD BACK, OR BYPASS IT. It is the only thing standing between a
 * misconfigured build and a staging bundle wearing production's identity — at which point this
 * function would report `production` from a staging build, the exact conflation the file exists to
 * prevent, arriving by a different door. Precision is not relaxation; passing everything is.
 */

/** Firebase project id → the environment name reported to analytics. */
const PROJECT_ENVIRONMENTS = {
  'qarasay-kz': 'production',
  'qarasay-staging': 'staging',
}

/**
 * The environment this client build belongs to.
 *
 * @returns {string} `production`, `staging`, the raw project id, or `unknown`.
 */
export function resolveClientEnv() {
  const projectId = process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID
  if (typeof projectId === 'string' && projectId.trim().length > 0) {
    const trimmed = projectId.trim()
    return PROJECT_ENVIRONMENTS[trimmed] || trimmed
  }
  // Reached only where the bundle was built without a Firebase project — a bare local run or a
  // test. `MY_ENVIRONMENT` is honoured here because in THAT context (plain Node) it does work, and
  // it is the variable a developer running one of the npm scripts has already set.
  const nodeEnv = process.env.MY_ENVIRONMENT
  if (typeof nodeEnv === 'string' && nodeEnv.trim().length > 0) return nodeEnv.trim()
  return 'unknown'
}
